import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClients } from '@/app/ApiProvider';
import type { components } from '@/api/types/api-gateway';

// S10 Inbox — subscription rule CRUD (PR-7 backend, PR-8 frontend).
// Keyed by ['subscriptions','list'] so the Subscriptions tab refreshes
// automatically when the user creates or deletes a rule.

export type Subscription = components['schemas']['Subscription'];
export type SubscriptionWriteRequest = components['schemas']['SubscriptionWriteRequest'];

export function useSubscriptions() {
  const clients = useApiClients();
  return useQuery({
    queryKey: ['subscriptions', 'list'],
    queryFn: async (): Promise<Subscription[]> => {
      const { data, error } = await clients.apiGateway.GET('/v1/users/me/subscriptions');
      if (error || !data) throw new Error('subscriptions failed');
      return data.items ?? [];
    },
    staleTime: 30_000,
  });
}

export function useCreateSubscription() {
  const clients = useApiClients();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: SubscriptionWriteRequest) => {
      const { data, error } = await clients.apiGateway.POST('/v1/users/me/subscriptions', {
        body: vars,
        params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
      });
      if (error) throw new Error('create subscription failed');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptions'] }),
  });
}

export function useDeleteSubscription() {
  const clients = useApiClients();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (subId: string) => {
      const { error } = await clients.apiGateway.DELETE('/v1/users/me/subscriptions/{subId}', {
        params: { path: { subId } },
      });
      if (error) throw new Error('delete subscription failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptions'] }),
  });
}
